import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Loader2, Eye, GraduationCap, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIB } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { STUDENT_STATUS_LABELS, CLIENT_STATUS_LABELS, ENTITY_STATUS_CLS } from "@/lib/prospectMeta";

const inputCls = "px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

function Badge({ status, labels }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs border ${ENTITY_STATUS_CLS[status] || "bg-slate-100"}`}>{labels[status] || status}</span>;
}

export default function SiswaKlien() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "klien" ? "klien" : "siswa");
  const [q, setQ] = useState("");
  const [students, setStudents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "siswa") { const { data } = await api.get("/students", { params: q ? { q } : {} }); setStudents(data.items); }
      else { const { data } = await api.get("/clients", { params: q ? { q } : {} }); setClients(data.items); }
    } catch { toast.error("Gagal memuat data."); }
    finally { setLoading(false); }
  }, [tab, q]);
  useEffect(() => { load(); }, [load]);

  const tabCls = (t) => `inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-[#f97316] text-[#0f2847]" : "border-transparent text-slate-500 hover:text-slate-700"}`;

  return (
    <AppLayout>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Siswa & Klien</h1>
        <p className="text-sm text-slate-500">Data hasil konversi prospek Berhasil/Deal</p>
      </div>

      <div className="flex items-center border-b border-slate-200 mb-4">
        <button onClick={() => setTab("siswa")} className={tabCls("siswa")} data-testid="tab-siswa"><GraduationCap className="h-4 w-4" /> Siswa</button>
        <button onClick={() => setTab("klien")} className={tabCls("klien")} data-testid="tab-klien"><Building2 className="h-4 w-4" /> Klien Lembaga</button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input className={`${inputCls} w-full pl-9`} placeholder="Cari nama, WA, program, ID…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="entity-search" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "siswa" ? (
            <table className="w-full text-sm" data-testid="students-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Nama</th><th className="text-left px-4 py-3">Orang Tua/Wali</th><th className="text-left px-4 py-3">WhatsApp</th><th className="text-left px-4 py-3">Program</th><th className="text-left px-4 py-3">Metode</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Bergabung</th><th className="text-left px-4 py-3">PIC</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
                : students.length === 0 ? <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400" data-testid="students-empty">Belum ada siswa. Konversikan prospek Berhasil/Deal.</td></tr>
                : students.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{s.student_no}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{s.full_name}</td>
                    <td className="px-4 py-3 text-slate-600">{s.guardian_name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{s.phone_normalized || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{s.program || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{s.mode || "-"}</td>
                    <td className="px-4 py-3"><Badge status={s.status} labels={STUDENT_STATUS_LABELS} /></td>
                    <td className="px-4 py-3 text-slate-600">{s.join_date || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{s.assigned_to_name || "-"}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => navigate(`/siswa/${s.id}`)} data-testid={`student-view-${s.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Detail/Edit"><Eye className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Lembaga</th><th className="text-left px-4 py-3">Jenis</th><th className="text-left px-4 py-3">Kontak/PIC</th><th className="text-left px-4 py-3">WhatsApp</th><th className="text-left px-4 py-3">Program</th><th className="text-left px-4 py-3">Peserta</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Bergabung</th><th className="text-left px-4 py-3">PIC IEC</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
                : clients.length === 0 ? <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400" data-testid="clients-empty">Belum ada klien lembaga.</td></tr>
                : clients.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.client_no}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{c.institution_name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.institution_type || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.contact_person || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.phone_normalized || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.program || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.participant_count || "-"}</td>
                    <td className="px-4 py-3"><Badge status={c.status} labels={CLIENT_STATUS_LABELS} /></td>
                    <td className="px-4 py-3 text-slate-600">{c.join_date || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{c.assigned_to_name || "-"}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => navigate(`/klien/${c.id}`)} data-testid={`client-view-${c.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Detail/Edit"><Eye className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
